import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Avatar,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Home as HomeIcon,
  LocationOn as LocationOnIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarTodayIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/Common/PageHeader';
import ContentPaper from '../../components/Common/ContentPaper';
import { Property, PropertyType } from '../../types';
import api from '../../utils/api';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../hooks/useApiCache';

const PendingActionsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [pendingProperties, setPendingProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchPendingProperties = async () => {
    try {
      setLoading(true);
      const response = await api.get<Property[]>('/properties/pending');
      setPendingProperties(response.data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch pending properties');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingProperties();
  }, []);

  const handleApprove = async (propertyId: string) => {
    try {
      setActionLoading(true);
      await api.patch(`/properties/${propertyId}/approve`);
      setPendingProperties((prev) => prev.filter((p) => p.id !== propertyId));
      setError('');
      // Invalidate properties cache to reflect changes in other pages
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROPERTIES });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to approve property');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedProperty) return;

    try {
      setActionLoading(true);
      await api.patch(`/properties/${selectedProperty.id}/reject`, {
        rejectionReason,
      });
      setPendingProperties((prev) => prev.filter((p) => p.id !== selectedProperty.id));
      setRejectDialogOpen(false);
      setRejectionReason('');
      setSelectedProperty(null);
      setError('');
      // Invalidate properties cache to reflect changes in other pages
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROPERTIES });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reject property');
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectDialog = (property: Property) => {
    setSelectedProperty(property);
    setRejectDialogOpen(true);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectionReason('');
    setSelectedProperty(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getPropertyTypeLabel = (type: PropertyType) => {
    return type.replace('_', ' ');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Pending Actions"
        subtitle={`${pendingProperties.length} pending approval${
          pendingProperties.length !== 1 ? 's' : ''
        }`}
        icon={<HomeIcon />}
      />

      <ContentPaper>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {pendingProperties.length === 0 ? (
          <Box textAlign="center" py={6}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No pending approvals
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              All properties have been reviewed
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {pendingProperties.map((property) => (
              <Grid item xs={12} key={property.id}>
                <Card
                  elevation={2}
                  sx={{
                    border: '2px solid',
                    borderColor: 'warning.main',
                    position: 'relative',
                  }}
                >
                  <Chip
                    label="PENDING APPROVAL"
                    color="warning"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      fontWeight: 'bold',
                    }}
                  />

                  <CardContent>
                    <Grid container spacing={3}>
                      {/* Property Image */}
                      <Grid item xs={12} md={4}>
                        {property.images && property.images.length > 0 ? (
                          <Box
                            component="img"
                            src={property.images[0]}
                            alt={property.title}
                            sx={{
                              width: '100%',
                              height: 250,
                              objectFit: 'cover',
                              borderRadius: 1,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: 250,
                              bgcolor: 'grey.200',
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <HomeIcon sx={{ fontSize: 60, color: 'grey.400' }} />
                          </Box>
                        )}
                      </Grid>

                      {/* Property Details */}
                      <Grid item xs={12} md={8}>
                        <Typography variant="h5" gutterBottom fontWeight="bold">
                          {property.title}
                        </Typography>

                        <Box display="flex" gap={1} mb={2}>
                          <Chip
                            label={getPropertyTypeLabel(property.propertyType)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={property.city}
                            size="small"
                            icon={<LocationOnIcon />}
                          />
                        </Box>

                        {property.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={2}
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {property.description}
                          </Typography>
                        )}

                        <Grid container spacing={2} mb={2}>
                          <Grid item xs={6} sm={3}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <AttachMoneyIcon color="primary" fontSize="small" />
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Price
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatPrice(property.price)}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>

                          {property.bedrooms && (
                            <Grid item xs={6} sm={3}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Bedrooms
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {property.bedrooms}
                                </Typography>
                              </Box>
                            </Grid>
                          )}

                          {property.bathrooms && (
                            <Grid item xs={6} sm={3}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Bathrooms
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {property.bathrooms}
                                </Typography>
                              </Box>
                            </Grid>
                          )}

                          {property.size && (
                            <Grid item xs={6} sm={3}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Size
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {property.size} m²
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        {/* Agent & Submission Info */}
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              <PersonIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Submitted by
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {property.createdBy
                                  ? `${property.createdBy.firstName} ${property.createdBy.lastName}`
                                  : property.agent?.user
                                  ? `${property.agent.user.firstName} ${property.agent.user.lastName}`
                                  : 'Unknown'}
                              </Typography>
                            </Box>
                          </Box>

                          <Box display="flex" alignItems="center" gap={1}>
                            <CalendarTodayIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(property.createdAt)}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Action Buttons */}
                        <Box display="flex" gap={2} mt={3}>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => handleApprove(property.id)}
                            disabled={actionLoading}
                            fullWidth
                          >
                            Approve
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<CancelIcon />}
                            onClick={() => openRejectDialog(property)}
                            disabled={actionLoading}
                            fullWidth
                          >
                            Reject
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </ContentPaper>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={closeRejectDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Property</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Please provide a reason for rejecting this property. This will be visible to
            the agent.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="e.g., Images are not clear, property details are incomplete..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRejectDialog} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={!rejectionReason.trim() || actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Reject Property'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingActionsPage;
